import { debounce } from 'lodash';

(function () {
    const perspectiveObj = {
        perspectiveCoords: null,
        __perspectiveMode: false,

        __resetPerspectiveImageSize: function () {
            if (!this.enablePerspective) return;
            const imgElement = this.item(0);
            imgElement.set({
                dirty: true
            });
            imgElement.setCoords();
        },

        __updatePerspectiveCoords: function (coords) {
            const item = this.item(0);
            item.perspectiveCoords = coords;
            this.perspectiveCoords = coords;
            item.set({
                dirty: true
            });
            item.setCoords();

            const filter = item.filters.find((filter) => filter instanceof fabric.Image.filters.Perspective);
            if (filter) {
                filter.updateCoords(coords);
                if (item._originalElement) {
                    item.applyFilters();
                }
            } else {
                item._addPerspectiveFilter(coords);
            }
            this.canvas.requestRenderAll();
        },

        onAddedCanvas: function () {
            const item = this.item(0);
            if (this.enablePerspective && this.perspectiveCoords.length > 0) {
                item.enablePerspective = true;
                item._addPerspectiveFilter(this.perspectiveCoords);
                this.__updatePerspectiveCoords(this.perspectiveCoords);
            }
        },

        _removePerspective: function () {
            const item = this.item(0);
            item._removePerspective();
            this._exitEditMode();
            this.canvas.setActiveObject(this);

            // clear perspective
            this.__perspectiveMode = false;
            this.enablePerspective = false;
            this.perspectiveCoords = null;
            item.set({
                dirty: true
            });
            item.setCoords();
        },

        __updateObjPerspectivePosition: function (obj, offsetX, offsetY, item) {
            const result = this._correctOffset(offsetX, offsetY);
            obj.left = this.left + result.x * item.scaleX;
            obj.top = this.top + result.y * item.scaleY;
        },

        togglePerspective: function (active = true) {
            this.__perspectiveMode = active;
            if (!active) {
                this._exitEditMode(null, true, true);
                return;
            }

            this.__backupVisible = this.visible;
            this.__backupLockMovementX = this.lockMovementX;
            this.__backupLockMovementY = this.lockMovementY;
            this.__backupCanvasSelection = this.canvas.selection;
            this.__backupOpacity = this.opacity;

            this.lockMovementX = true;
            this.lockMovementY = true;

            // eslint-disable-next-line @typescript-eslint/no-this-alias
            const _scope = this;
            const item = this.item(0);
            item.enablePerspective = this.enablePerspective;
            item.perspectiveCoords = this.perspectiveCoords?.length > 0 ? this.perspectiveCoords : null;
            const boundSize = this._getInitialBoundsRectangleSize();
            const obj = {
                enablePerspective: true,
                zIndex: this.zIndex + 1,
                hasBorders: true,
                hasControls: true,
                lockMovementX: true,
                lockMovementY: true,
                width: boundSize.width,
                height: boundSize.height,
                angle: this.angle,
                left: this.left,
                top: this.top,
                opacity: 0,
                scaleX: 1,
                scaleY: 1,
                filters: [],
                originX: 'center',
                originY: 'center',
                isPerspectiveControl: true,
                __limitWidth: this.width,
                __limitHeight: this.height,
                __originalLeft: this.left,
                __originalTop: this.top
            };
            this._imagePerspective = new fabric.MaskedImage(this.originalElement, obj);
            this._imagePerspective.setCoords();

            const updatePerspectiveDebounce = debounce(this.__updatePerspectiveCoords.bind(this), 500);

            this._imagePerspective.on({
                'object:perspective:update': function ({ perspectiveCoords }) {
                    updatePerspectiveDebounce(perspectiveCoords);
                },
                'object:perspective:clickdown': function () {
                    _scope.opacity = 0.8;
                },
                mouseup: function () {
                    _scope.opacity = 1;
                }
            });

            this.canvas.add(this._imagePerspective);
            this.canvas.setActiveObject(this._imagePerspective);
            this._imagePerspective.togglePerspective(true, true, item.perspectiveCoords);
            this._imagePerspective.on('deselected', this._exitEditMode.bind(this));
            this.canvas.requestRenderAll();
        },

        _exitEditMode: function (_event, force = false, selection = false) {
            if (!this.__perspectiveMode && !force) return;

            if (typeof this.__backupLockMovementX !== 'undefined') {
                this.lockMovementX = this.__backupLockMovementX;
                this.lockMovementY = this.__backupLockMovementY;
                this.opacity = this.__backupOpacity;
                this.canvas.selection = this.__backupCanvasSelection;
            }

            if (this.canvas && this._imagePerspective && this._imagePerspective.__boundingBox) {
                this.canvas.remove(this._imagePerspective.__boundingBox);
                this._imagePerspective.__boundingBox = null;
            }

            this._clearImagePerspective();
            this.setCoords();
            //this.updateFromGroupScaling();

            // Set active object
            if (selection) this.canvas.setActiveObject(this);
            this.canvas.requestRenderAll();
            this.__backupLockMovementX = undefined;
            this.__backupLockMovementY = undefined;
            this.__backupVisible = undefined;
            this.__perspectiveMode = false;
        },

        _clearImagePerspective: function () {
            if (!this._imagePerspective) return;
            this._imagePerspective.off('deselected', this._exitEditMode);
            this._imagePerspective.controls = {
                ...fabric.MaskedImage.prototype.controls
            };
            this.canvas.remove(this._imagePerspective);
            this._imagePerspective?._removePerspective();
            this._imagePerspective = null;
        }
    };
    fabric.util.object.extend(fabric.ImagePlaceholder.prototype, perspectiveObj);

    const constrainToBoundingBox = (x, y, boundingBox) => {
        const center = new fabric.Point(boundingBox.left, boundingBox.top);
        const halfWidth = boundingBox.width / 2;
        const halfHeight = boundingBox.height / 2;

        // Crear un punto con las coordenadas dadas
        let point = new fabric.Point(x, y);

        // Rotar el punto en la dirección opuesta a la rotación del boundingBox
        point = fabric.util.rotatePoint(point, center, fabric.util.degreesToRadians(-boundingBox.angle));

        // Limitar las coordenadas al boundingBox
        point.x = Math.min(Math.max(point.x, center.x - halfWidth), center.x + halfWidth);
        point.y = Math.min(Math.max(point.y, center.y - halfHeight), center.y + halfHeight);

        // Rotar el punto de vuelta
        return fabric.util.rotatePoint(point, center, fabric.util.degreesToRadians(boundingBox.angle));
    };

    fabric.util.object.extend(fabric.MaskedImage.prototype, {
        perspectiveCoords: null,
        pathOffset: null,
        __perspectiveMode: false,

        setInitialPerspective: function () {
            const defaultPercentageCoords = [
                [-50, -50], // top left
                [50, -50], // top right
                [50, 50], // bottom right
                [-50, 50] // bottom left
            ];
            this.perspectiveCoords = defaultPercentageCoords;
            return defaultPercentageCoords;
        },

        _removePerspective: function () {
            this.filters = this.filters.filter((filter) => !(filter instanceof fabric.Image.filters.Perspective));
            this.applyFilters();
            this.perspectiveCoords = null;
        },

        _addPerspectiveFilter: function (newCoords) {
            const coords = newCoords || this.perspectiveCoords;
            try {
                const filterExist = this.filters.find((filter) => filter instanceof fabric.Image.filters.Perspective);
                if (filterExist) {
                    filterExist.updateCoords(coords);
                    this.applyFilters();
                    return;
                }
                const perspectiveFilter = new fabric.Image.filters.Perspective({
                    perspectiveCoords: coords
                });

                this?.filters?.push(perspectiveFilter);
                if (this._originalElement) this?.applyFilters();
            } catch (error) {
                console.error('_addPerspectiveFilter', coords, error, this._originalElement);
            }
        },

        togglePerspective: function (active = true, showControls = false, coords = null) {
            this.__perspectiveMode = active;
            if (!active) {
                this.lockMovementX = false;
                this.lockMovementY = false;
                this.canvas.requestRenderAll();
                return;
            }

            if (coords) {
                this.perspectiveCoords = coords;
            } else if (!this.perspectiveCoords) {
                this.setInitialPerspective();
            }

            if (!showControls) {
                return;
            }
            this.__boundingBox = new fabric.Rect({
                strokeDashArray: [],
                width: this.__limitWidth,
                height: this.__limitHeight,
                left: this.__originalLeft,
                top: this.__originalTop,
                originX: 'center',
                originY: 'center',
                fill: 'rgba(0, 0, 0, 0)',
                stroke: '#000',
                strokeWidth: 2,
                selectable: false,
                angle: this.angle
            });
            this.canvas.add(this.__boundingBox);
            this.__boundingBox.setCoords();

            const w = this.getScaledWidth();
            const h = this.getScaledHeight();
            const percentageCoords = this.perspectiveCoords.map((coord) => [
                (coord[0] / 100) * w,
                (coord[1] / 100) * h
            ]);

            // eslint-disable-next-line @typescript-eslint/no-this-alias
            const _scope = this;
            const lastControl = this.perspectiveCoords.length - 1;
            this.controls = percentageCoords.reduce((acc, coord, index) => {
                const anchorIndex = index > 0 ? index - 1 : lastControl;
                const name = `prs${anchorIndex + 1}`;

                acc[name] = new fabric.Control({
                    name,
                    x: -0.5,
                    y: -0.5,
                    offsetX: 0,
                    offsetY: 0,
                    cursorStyleHandler: () => 'cell',
                    mouseDownHandler: function (_, t) {
                        const { target } = t;
                        if (!target) return;
                        target.fire('object:perspective:clickdown', { target });
                    },
                    positionHandler: function (_, _2, fabricObject) {
                        const scalarX = fabricObject.flipX ? -1 : 1;
                        const scalarY = fabricObject.flipY ? -1 : 1;

                        const point = fabric.util.transformPoint(
                            {
                                x: coord[0] * scalarX,
                                y: coord[1] * scalarY
                            },
                            fabric.util.multiplyTransformMatrices(
                                fabricObject.canvas.viewportTransform,
                                fabricObject.calcTransformMatrix()
                            )
                        );

                        return point;
                    },
                    actionHandler: function (_, transform, x, y) {
                        const { target } = transform;

                        // bounding box with angle
                        {
                            const constrained = constrainToBoundingBox(x, y, _scope.__boundingBox);
                            x = constrained.x;
                            y = constrained.y;
                        }

                        const localPoint = target.toLocalPoint(new fabric.Point(x, y), 'center', 'center');
                        const noTransformDimension = target._getNonTransformedDimensions();
                        const newTransform = target._getTransformedDimensions(0, 0);

                        coord[0] = Math.round((localPoint.x * noTransformDimension.x) / newTransform.x);
                        coord[1] = Math.round((localPoint.y * noTransformDimension.y) / newTransform.y);

                        const newPerspectiveCoords = percentageCoords.map((c) => [(c[0] / w) * 100, (c[1] / h) * 100]);
                        _scope.fire('object:perspective:update', {
                            target: _scope,
                            perspectiveCoords: newPerspectiveCoords
                        });
                        _scope.fire('object:modified', { target: _scope, transform: transform });
                        return true;
                    },
                    render: function (ctx, left, top, _, fabricObject) {
                        const zoom = fabricObject.canvas.getZoom();
                        const scalarX = fabricObject.scaleX * zoom;
                        const scalarY = fabricObject.scaleY * zoom;

                        ctx.save();
                        ctx.translate(left, top);
                        ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle));
                        ctx.beginPath();

                        ctx.moveTo(0, 0);
                        ctx.strokeStyle = '#E863F4';

                        if (percentageCoords[index + 1]) {
                            ctx.strokeStyle = '#E863F4';
                            ctx.lineTo(
                                (percentageCoords[index + 1][0] - coord[0]) * scalarX,
                                (percentageCoords[index + 1][1] - coord[1]) * scalarY
                            );
                        } else {
                            ctx.lineTo(
                                (percentageCoords[0][0] - coord[0]) * scalarX,
                                (percentageCoords[0][1] - coord[1]) * scalarY
                            );
                        }
                        ctx.stroke();
                        ctx.beginPath();
                        ctx.arc(0, 0, 6, 0, Math.PI * 2);
                        ctx.closePath();
                        ctx.fillStyle = '#E863F4';
                        ctx.fill();
                        ctx.stroke();
                        ctx.restore();
                    },
                    actionName: 'perspective-coords'
                });
                return acc;
            }, {});
        }
    });

    /*fabric.util.object.extend(fabric.MaskedImage.prototype, {
        _renderFill: function (ctx) {
            if (!this.enablePerspective) {
                this.callSuper('_renderFill', ctx);
                return;
            }
            var elementToDraw = this._element;
            if (!elementToDraw) return;

            ctx.save();
            const elWidth = elementToDraw.naturalWidth || elementToDraw.width;
            const elHeight = elementToDraw.naturalHeight || elementToDraw.height;
            const width = this.width;
            const height = this.height;

            ctx.translate(-width / 2, -height / 2);

            // get the scale
            const scale = Math.min(width / elWidth, height / elHeight);

            // get the top left position of the image
            const x = width / 2 - (elWidth / 2) * scale;
            const y = height / 2 - (elHeight / 2) * scale;

            ctx.drawImage(elementToDraw, x, y, elWidth * scale, elHeight * scale);

            ctx.restore();
        }
    });*/
})();
